import { DateTime } from 'luxon';
import EventDTO from '../models/event';
import { OrderDTO } from '../models/order';
import type { PrintTicketData } from '../components/PrintTicket';

export const DEFAULT_PRINT_PREPARE_DELAY_MS = 4000;

/**
 * Label of a numbered place on a ticket. Items of a table sold whole share the
 * seat and carry seat_index: "Mesa 12 · Persona 3 de 8". Mirrors the backend's
 * formatSeatLabel so printed and emailed tickets read the same.
 */
export function formatSeatLabel(seatName: string | null | undefined, seatIndex?: number | null, seatCount?: number | null): string {
  if (!seatName) {
    return '';
  }
  if (!seatIndex || !seatCount || seatCount < 2) {
    return seatName;
  }
  return `${seatName} · Persona ${seatIndex} de ${seatCount}`;
}

const itemSeatLabel = (item: OrderDTO['items'][number]) => formatSeatLabel(
  item.event_seat?.name,
  item.seat_index,
  item.ticket?.items_create,
) || 'General';
export const DEFAULT_PRINT_DIALOG_DELAY_MS = 500;

export interface BuildPrintTicketDataListArgs {
  order: OrderDTO;
  event: EventDTO;
  qrImageData?: { qr_base64: string[] } | null;
  subscribedEvents: EventDTO[];
  onlyItemId?: string;
}

/** Builds printable ticket rows (including subscription-bundle expansion) aligned with qr_base64 indices. */
export function buildPrintTicketDataList({
  order,
  event,
  qrImageData,
  subscribedEvents,
  onlyItemId,
}: BuildPrintTicketDataListArgs): PrintTicketData[] {
  const qrDataUrlForItem = (itemIndex: number) => {
    const b = qrImageData?.qr_base64?.[itemIndex] ?? qrImageData?.qr_base64?.[0];
    return b ? `data:image/png;base64,${b}` : undefined;
  };

  const formatEventFields = (ev: EventDTO) => {
    const d = DateTime.fromISO(ev.date).setLocale('es');
    return {
      eventName: ev.name,
      place: ev.place,
      date: d.toFormat('dd/MM/yyyy'),
      time: d.toFormat('HH:mm'),
    };
  };

  const itemsSource = onlyItemId
    ? order.items.filter((item) => item.id === onlyItemId)
    : order.items;

  if (itemsSource.length === 0) {
    return [];
  }

  const withSubscription = event.has_subscription && event.prints_subscriptions && subscribedEvents.length > 0;
  const eventsToShow = subscribedEvents
    .filter((se) => se.id !== event.id)
    .sort((a, b) => DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis());

  if (withSubscription && eventsToShow.length > 0) {
    return itemsSource.flatMap((item) => {
      const itemIndex = order.items.findIndex((i) => i.id === item.id);
      const base = {
        seat: itemSeatLabel(item),
        row: item.ticket?.name || '-',
        price: `$${item.ticket?.price?.toFixed(2) || '0.00'}`,
        qrCode: qrDataUrlForItem(itemIndex),
      };
      return eventsToShow.map((ev) => ({
        id: `${item.id}-${ev.id}`,
        ...formatEventFields(ev),
        ...base,
      }));
    });
  }

  const main = formatEventFields(event);
  return itemsSource.map((item) => {
    const index = order.items.findIndex((i) => i.id === item.id);
    return {
      id: item.id,
      ...main,
      seat: itemSeatLabel(item),
      row: item.ticket?.name || '-',
      price: `$${item.ticket?.price?.toFixed(2) || '0.00'}`,
      qrCode: qrDataUrlForItem(index),
    };
  });
}

export interface PrintTicketHtmlInNewWindowOptions {
  documentTitle: string;
  prepareDelayMs?: number;
  printDelayMs?: number;
  onPrepareStart?: () => void;
  onComplete?: () => void;
}

/**
 * Opens a new window with the same styles as the shell, injects printable HTML from `getRoot()`, then triggers print.
 * Resolves root inside `prepareDelayMs` so callers can mount off-screen content before capture.
 */
export function printTicketHtmlInNewWindow(
  getRoot: () => HTMLElement | null,
  {
    documentTitle,
    prepareDelayMs = DEFAULT_PRINT_PREPARE_DELAY_MS,
    printDelayMs = DEFAULT_PRINT_DIALOG_DELAY_MS,
    onPrepareStart,
    onComplete,
  }: PrintTicketHtmlInNewWindowOptions,
): void {
  onPrepareStart?.();

  window.setTimeout(() => {
    const printContent = getRoot();
    if (!printContent) {
      onComplete?.();
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      onComplete?.();
      return;
    }

    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((el) => el.outerHTML)
      .join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>${documentTitle}</title>
          ${styles}
          <style>
            @page { margin: 0; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();

    printWindow.focus();
    window.setTimeout(() => {
      printWindow.print();
      onComplete?.();
    }, printDelayMs);
  }, prepareDelayMs);
}
